import { useEffect } from 'react'
import { Nav, Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../components/auth/useAuth'
import { NavLink } from 'react-router-dom'
import { getDashboardMe, selectStudent } from '../../actions/usersActions'

export default function Sidebar() {
    const { t } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch = useDispatch()
    const { dashboard, selectedStudentId } = useSelector(state => state.users)

    const isParent = clientauth?.rol === 'parent'
    const students = dashboard?.students || []
    const currentStudent = students.find(s => s.id === selectedStudentId) || students[0] || null

    useEffect(() => {
        if (isParent && !dashboard) dispatch(getDashboardMe())
    }, [dispatch, isParent])

    const initials = (name) => {
        if (!name) return '—'
        return name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('')
    }

    return (
        <div className='sidebar d-flex flex-column pt-3'>
            <div className='sidebar-logo d-flex align-items-center justify-content-center'>
                <img src="/images/logotipo-albany.png" alt="Logotipo Albany" width={60} className='logo-sidebar' />
                <div className='mx-3'>
                    <h5 className='fw-bold text-black'>Albany School</h5>
                    {clientauth?.rol === 'student' &&
                        <p className='text-muted mb-0'>{t('admin.dashboard.studentportal')}</p>
                    }
                    {isParent &&
                        <p className='text-muted mb-0'>{t('admin.dashboard.parentportal')}</p>
                    }
                </div>
            </div>

            <hr className='mb-0'></hr>

            {isParent &&
                <div className='mt-4 px-3'>
                    <p className='text-muted mb-1 px-2' style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        {t('admin.dashboard.selectedstudent')}
                    </p>
                    <Dropdown onSelect={(k) => dispatch(selectStudent(k))} className='w-100'>
                        <Dropdown.Toggle variant="" className='sidebar-link w-100 d-flex align-items-center justify-content-between text-start overflow-hidden'
                            style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none', padding: '10px 20px' }}>
                            <div className='d-flex align-items-center flex-grow-1' style={{ minWidth: 0 }}>
                                <div className='rounded-circle bg-light bg-opacity-75 d-flex align-items-center justify-content-center me-3 flex-shrink-0 overflow-hidden'
                                    style={{ width: '36px', height: '36px', fontSize: '13px', fontWeight: 700, color: '#6c757d' }}>
                                    {currentStudent?.avatar
                                        ? <img src={currentStudent.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'none' }} />
                                        : initials(currentStudent?.name)
                                    }
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <p className='mb-0 fw-bold text-truncate' style={{ fontSize: '14px' }}>{currentStudent?.name || '—'}</p>
                                    <p className='mb-0 text-muted text-truncate' style={{ fontSize: '12px' }}>{currentStudent?.courseName || ''}</p>
                                </div>
                            </div>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className='w-100'>
                            {students.length === 0 && <Dropdown.ItemText>—</Dropdown.ItemText>}
                            {students.map(s => (
                                <Dropdown.Item key={s.id} eventKey={s.id} active={s.id === currentStudent?.id}>
                                    {s.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            }

            <Nav className='flex-column mt-4 px-3 gap-2'>
                <Nav.Link as={NavLink} to={`/${clientauth?.rol}/dashboard`} className='sidebar-link'>
                    <div className='d-flex'>
                        <img src="/images/home.svg" alt="Home" className='me-3' />
                        <div>
                            <p className='mb-0 fw-bold'>{t('admin.dashboard.index')}</p>
                            <p className='mb-0 text-muted small'>{t('admin.dashboard.indexdescription')}</p>
                        </div>
                    </div>
                </Nav.Link>
                {isParent &&
                    <Nav.Link as={NavLink} to={`/${clientauth?.rol}/follow-up`} className='sidebar-link'>
                        <div className='d-flex'>
                            <img src="/images/pencil.svg" alt="seguimiento" className='me-3' />
                            <div>
                                <p className='mb-0 fw-bold'>{t('admin.dashboard.followup')}</p>
                                <p className='mb-0 text-muted small'>{t('admin.dashboard.followupdescription')}</p>
                            </div>
                        </div>
                    </Nav.Link>
                }
                <Nav.Link as={NavLink} to={`/${clientauth?.rol}/academic`} className='sidebar-link'>
                    <div className='d-flex'>
                        <img src="/images/openbook.svg" alt="openbook" className='me-3' />
                        <div>
                            <p className='mb-0 fw-bold'>{t('admin.dashboard.academic')}</p>
                            <p className='mb-0 text-muted small'>{t('admin.dashboard.academicdescription')}</p>
                        </div>
                    </div>
                </Nav.Link>
                <Nav.Link as={NavLink} to={`/${clientauth?.rol}/communications`} className='sidebar-link'>
                    <div className='d-flex'>
                        <img src="/images/chat.svg" alt="chat" className='me-3' />
                        <div>
                            <p className='mb-0 fw-bold'>{t('admin.dashboard.comunications')}</p>
                            <p className='mb-0 text-muted small'>{t('admin.dashboard.comunicationsdescription')}</p>
                        </div>
                    </div>
                </Nav.Link>
                <Nav.Link as={NavLink} to={`/${clientauth?.rol}/administration`} className='sidebar-link'>
                    <div className='d-flex'>
                        <img src="/images/document.svg" alt="document" className='me-3' />
                        <div>
                            <p className='mb-0 fw-bold'>{t('admin.dashboard.administration')}</p>
                            <p className='mb-0 text-muted small'>{t('admin.dashboard.administrationdescription')}</p>
                        </div>
                    </div>
                </Nav.Link>
                <Nav.Link as={NavLink} to={`/${clientauth?.rol}/profile`} className='sidebar-link'>
                    <div className='d-flex'>
                        <img src="/images/user.svg" alt="user" className='me-3' />
                        <div>
                            <p className='mb-0 fw-bold'>{t('admin.dashboard.profile')}</p>
                            <p className='mb-0 text-muted small'>{t('admin.dashboard.profiledescription')}</p>
                        </div>
                    </div>
                </Nav.Link>
            </Nav>
        </div >
    )
}
